import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import { ApplicationEnum } from "../../../../../enums/ApplicationEnum";
import { formatName } from "../../../../../common/format-name";
import { Gender } from "../../../../../enums/GenderEnum";
import { LevelOfCareFilters } from "../../../../../enums/LevelOfCareFilterEnum";
import { PatientStatusEnum, PermissionActionEnum, Veterans } from "../../../../../enums/enums";
import { CodeStatus } from "../../../../../enums/ReferralEnums/CodeStatusEnum";
import { ProvidersService } from "../../../../../services/providers-service";
import { BranchesService } from "../../../../../services/branches-service";
import { formatDate, formatPhoneNumber } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { ClinicalPermissionEnum } from "../../../../../enums/ClinicalPermissionsEnum";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";
import { NavigationService } from "../../../../../services/navigation.service";
import { AnalyticsCategory } from "../../../../../services/analytics.service";
import config from "../../../../../common/config";

import EmptyTextHandler from "../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        EmptyTextHandler
    },

    props: {
        agencyId: {
            type: String
        },
        application: {
            type: Number
        },
        selectedPatientDetails: {
            default: null
        }
    },
    data(vm) {
        return {
            _navigationService: null as NavigationService,
            _permissionManager: null as PermissionManager,
            _branchesService: null as BranchesService,
            _providersService: null as ProvidersService,
            patientBranch: null,
            isDoNotResuscitate: false,
            isDoNotIntubate: false,
            isDoNotHospitalize: false,
            isTagsEnabled: false,
            patientTags: "",
            genderEnum: Gender,
            levelOfCareEnum: LevelOfCareFilters,
            hasAddress: false,
            patientAddressLink: "",
            displayAddress: "",
            terminalDiagTooltip: "",
            canShowBenefitPeriodManager: false,
            terminalDiagnosis: null,
            medicaidLabel: "",
            isDischarged: false,
            patientSpecialtyPrograms: "",
            ApplicationEnum,
            formatName,
            formatPhoneNumber,
            formatDate
        };
    },
    async created() {
        this._providersService = Container.instance.get(ProvidersService);
        this._branchesService = Container.instance.get(BranchesService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._navigationService = Container.instance.get(NavigationService);
        this.canShowBenefitPeriodManager = this._permissionManager.checkPermission(
            ClinicalPermissionEnum.ManageBenefitPeriod,
            PermissionActionEnum.canView
        );
        this.initAdvancedCarePlanDirectives();
        this.initTerminalDiagnosis();
        this.initMedicaidLabel();
        this.isDischarged =
            this.selectedPatientDetails.status === PatientStatusEnum.discharged ||
            this.selectedPatientDetails.status === PatientStatusEnum.deceased;
        let allBranches = await this._branchesService.getAllBranches();
        this.patientBranch = allBranches.find((branch) => branch.id === this.selectedPatientDetails?.branchId);
        this.patientTags = this.selectedPatientDetails.patientTags?.map((tag) => tag.name).join(", ");
        // need to comment once BE is fixed
        // let specialtyPrograms = this.selectedPatientDetails.specialtyPrograms.map((program) => program.name);
        // this.patientSpecialtyPrograms = specialtyPrograms.join(",");
    },
    mounted() {
        // uncomment once BE is fixed
        // this.getIsTagsEnabled();
        this.hasAddress =
            !!this.selectedPatientDetails.addressLine1 &&
            !!this.selectedPatientDetails.city &&
            !!this.selectedPatientDetails.state;
        let zipCode = this.selectedPatientDetails.zipCode?.slice(0, 5);
        if (this.hasAddress) {
            if (this.selectedPatientDetails.addressLine2) {
                this.patientAddressLink = `https://www.google.com/maps/place/${
                    this.selectedPatientDetails.addressLine1
                },${encodeURIComponent(this.selectedPatientDetails.addressLine2)},${this.selectedPatientDetails.city},${
                    this.selectedPatientDetails.state
                }, ${zipCode}`;
                this.displayAddress = `${this.selectedPatientDetails.addressLine1}, ${this.selectedPatientDetails.addressLine2}, ${this.selectedPatientDetails.city}, ${this.selectedPatientDetails.state}, ${zipCode}`;
            } else {
                this.patientAddressLink = `https://www.google.com/maps/place/${this.selectedPatientDetails.addressLine1},${this.selectedPatientDetails.city},${this.selectedPatientDetails.state}, ${zipCode}`;
                this.displayAddress = `${this.selectedPatientDetails.addressLine1}, ${this.selectedPatientDetails.city}, ${this.selectedPatientDetails.state}, ${zipCode}`;
            }
        }
    },
    computed: {
        age() {
            if (!!this.selectedPatientDetails?.dateOfBirth) {
                let today = moment();
                let mDob = moment(this.selectedPatientDetails.dateOfBirth);
                let age = today.diff(mDob, "years");
                if (age < 2) {
                    let newAge = today.diff(mDob, "months");
                    if (newAge < 1) {
                        newAge = today.diff(mDob, "days");
                        return `${newAge} Days Old`;
                    }
                    return `${newAge} Months Old`;
                }
                return `${age} Years Old`;
            } else {
                return "";
            }
        },
        isPalliativeCare() {
            return this.application === ApplicationEnum.AxxessPalliative;
        },
        veteranLabel() {
            let veteranValue = this.selectedPatientDetails.veterans?.map((item) => Veterans.getKeyUsingValue(item));
            return veteranValue.join(", ");
        },
        allergyCount() {
            return this.selectedPatientDetails.allergies?.filter((allergy) => !allergy.endDate).length;
        },
        isPendingPatient() {
            return this.selectedPatientDetails.status === PatientStatusEnum.pending;
        }
    },
    methods: {
        async initAdvancedCarePlanDirectives() {
            [this.isDoNotResuscitate, this.isDoNotIntubate, this.isDoNotHospitalize] = [false, false, false];
            for (const directiveNumber of this.selectedPatientDetails.codeStatus) {
                const directiveName = (CodeStatus.getKeyUsingValue(directiveNumber) as string).toLocaleLowerCase();
                switch (directiveName) {
                    case "do not resuscitate":
                        this.isDoNotResuscitate = true;
                        break;
                    case "do not intubate":
                        this.isDoNotIntubate = true;
                        break;
                    case "do not hospitalize":
                        this.isDoNotHospitalize = true;
                        break;
                    default:
                        break;
                }
            }
        },
        async getIsTagsEnabled() {
            const payload = {
                agencyId: this.agencyId,
                application: this.application
            };
            let patientTagsSettings = await this._providersService.getOperationSettings(
                this.selectedPatientDetails.providerId,
                payload
            );
            this.isTagsEnabled = patientTagsSettings.hasTagEnabled;
        },
        initTerminalDiagnosis() {
            if (this.selectedPatientDetails?.diagnoses?.length > 0) {
                this.terminalDiagnosis = this.selectedPatientDetails.diagnoses.find((diagnosis) => {
                    return diagnosis.order === 1 && !!diagnosis.code && !!diagnosis.description;
                });
                if (!!this.terminalDiagnosis) {
                    this.terminalDiagTooltip = `${this.terminalDiagnosis.code} ${this.terminalDiagnosis.description}`;
                }
            } else {
                this.terminalDiagnosis = null;
            }
        },
        initMedicaidLabel() {
            if (!!this.selectedPatientDetails.medicaidNumber) {
                if (this.selectedPatientDetails.medicaidNumber == "+") {
                    this.medicaidLabel = "Pending";
                } else if (this.selectedPatientDetails.medicaidNumber == "N") {
                    this.medicaidLabel = "Not a Medicaid Recipient";
                } else {
                    this.medicaidLabel = this.selectedPatientDetails.medicaidNumber;
                }
            } else {
                this.medicaidLabel = null;
            }
        },
        handlePatientNameClick() {
            if (this.application === ApplicationEnum.AxxessHospiceFE) {
                let redirectResponse;
                let action = "";

                redirectResponse = {
                    redirectUrl: `${getHospiceRedirectionUrl(this.agencyId)}/patients/patient-chart/${this.selectedPatientDetails.id}?accountId=${this.agencyId}`,
                    isSuccessful: true
                };
                action = "AxxessHospice";
                this._navigationService.redirectOnNewTab(redirectResponse, {
                    category: AnalyticsCategory.CentralCalendarNavigation,
                    action
                });
            } else {
                window.open(
                    `${config.palliativeUrl}/patients/patient-chart/${this.selectedPatientDetails.id}?accountId=${this.agencyId}`
                );
            }
        }
    }
});
